<script setup>
import BasePreferenceTemplate from '@/components/preference/templates/BasePreferenceTemplate.vue'
import CheckboxPreferenceList from '@/components/preference/CheckboxPreferenceList.vue'
import PreferenceLogo from '@/components/preference/PreferenceLogo.vue'
import AutoSaveStatus from '@/components/preference/elements/AutoSaveStatus.vue'

import { withProps } from '@/components/preference/templates/preferenceTemplate.js'

defineProps({ ...withProps() })
defineEmits(['save', 'userEmail', 'update'])
defineOptions({ inheritAttrs: false })
</script>

<template>
  <BasePreferenceTemplate
    v-bind="{ ...$props, ...$attrs }"
    :list-component="CheckboxPreferenceList"
    @save="$emit('save')"
    @user-email="$emit('userEmail', $event)"
    @update="$emit('update')"
  >
    <template #form-step="{ logo, attrs, name, chosenEmail, updateChosenEmail, alertMsg, setEmail }">
      <div class="flex items-center gap-3 mx-6">
        <PreferenceLogo :url="logo" />
        <h1 class="text-xl font-semibold">{{ attrs?.tag_subject || $t('pref_page.title') || 'Update your preferences' }}</h1>
      </div>
      <p class="text-gray-400 mx-6 mt-1">{{ attrs?.tag_pre_name || $t('pref_page.header') || 'for ' }}{{ name }}</p>

      <form class="text-center mt-5" @submit="setEmail($event, chosenEmail)">
        <div class="flex mx-6 text-start items-center">
          <div class="mb-6 w-full">
            <h2 class="text-lg font-medium">{{ $t('pref_page_email_form.form_title') }}</h2>
            <input
              id="email"
              :value="chosenEmail"
              type="email"
              name="email"
              class="block w-full rounded-md border-0 py-1.5 px-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              :placeholder="$t('pref_page_email_form.form_placeholder').replace('[at]', '@')"
              @input="updateChosenEmail($event.target.value)"
            />
            <span v-if="alertMsg" class="text-red-500 text-xs mt-1 block">{{ alertMsg }}</span>
          </div>
        </div>

        <button
          type="submit"
          class="rounded-md px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 w-[90%]"
          :style="{ backgroundColor: attrs?.primary_color || '#0099d0' }"
        >
          {{ $t('pref_page_email_form.form_save_btn') }}
        </button>
      </form>
    </template>

    <template #preferences-header="{ logo, attrs, name }">
      <div class="flex items-center gap-3 mx-6">
        <PreferenceLogo :url="logo" />
        <div>
          <h2 class="text-xl font-semibold">{{ attrs?.tag_subject || $t('pref_page.title') || 'Update your preferences' }}</h2>
          <p class="text-gray-400 text-sm">{{ attrs?.tag_pre_name || $t('pref_page.header') || 'for ' }}{{ name }}</p>
        </div>
      </div>
    </template>

    <template #save-feedback="{ attrs, saveStatus }">
      <AutoSaveStatus v-if="attrs?.auto_save" :status="saveStatus" :color="attrs?.primary_color" />
    </template>

    <template #action-buttons="{ attrs, disabled, updateGlobal }">
      <footer class="mx-6 mb-6 flex justify-center space-x-4">
        <button
          v-if="attrs.unsubscribe_all"
          class="rounded-md px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 bg-white hover:bg-gray-50 w-full"
          @click="!disabled ? updateGlobal() : null"
        >
          {{ $t('pref_page.unsubscribe_btn') }}
        </button>
        <button
          v-if="!attrs?.auto_save"
          class="rounded-md px-4 py-2.5 text-sm font-semibold text-white shadow-sm w-full"
          :style="{ backgroundColor: attrs?.primary_color || '#0099d0' }"
          @click="$emit('save')"
        >
          {{ $t('pref_page.save_btn') }}
        </button>
      </footer>
    </template>
  </BasePreferenceTemplate>
</template>
